import React, { useState, useEffect } from "react";
import { Button, Card, CardContent, Stack, TextField, Typography, CircularProgress, Box, Grid, Tabs, Tab, AppBar } from "@mui/material";
import { useFormik, FormikProvider, Form } from "formik";
import * as Yup from "yup";
import { EMAIL_REGEX, IMAGE_BASE, URL_REGEX } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { apiAuth } from "../../services/AllServices";
import { useLocation, useNavigate } from "react-router-dom";

function GlobalSetting() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const settingData = async () => {
    try {
      setLoading(true);
      const response = await apiAuth.getSetting();
      setData(response.data.data);
      if (response.data.data?.logo) {
        setLogoPreview(IMAGE_BASE + response.data.data.logo);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    settingData();
  }, []);

  const initialValues = {
    instaLink: data?.instaLink ?? "",
    fbLink: data?.fbLink ?? "",
    twitterLink: data?.twitterLink ?? "",
    linkedInLink: data?.linkedInLink ?? "",
    contactUsEmail: data?.contactUsEmail ?? "",
    contestCommission: data?.contestCommission ?? "",
    description: data?.description || "",
    location: data?.location || "",
    phone: data?.phone || "",
  };

  const validationSchema = Yup.object().shape({
    instaLink: Yup.string().trim().required("Instagram Link is required").matches(URL_REGEX, "Instagram link must be in the format: https://<domain>.com/"),
    fbLink: Yup.string().trim().required("Facebook Link is required").matches(URL_REGEX, "Facebook link must be in the format: https://<domain>.com/"),
    twitterLink: Yup.string().trim().required("Twitter Link is required").matches(URL_REGEX, "Twitter link must be in the format: https://<domain>.com/"),
    linkedInLink: Yup.string().trim().required("LinkedIn Link is required").matches(URL_REGEX, "LinkedIn link must be in the format: https://<domain>.com/"),
    });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = new FormData();
        formData.append("instaLink", values.instaLink.trim());
        formData.append("fbLink", values.fbLink.trim());
        formData.append("twitterLink", values.twitterLink.trim());
        formData.append("linkedInLink", values.linkedInLink.trim());
        formData.append("contactUsEmail", values.contactUsEmail.trim());
        formData.append("contestCommission", values.contestCommission || "");
        formData.append("description", values?.description || "");
        formData.append("phone", values?.phone || "");
        formData.append("imagePath", "logo");

        if (logoFile) {
          formData.append("logo", logoFile);
        }

        const response = await apiAuth.updateSetting(formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.status === 200) {
          toast.success(response?.data?.message);
          settingData();
        }
      } catch (err) {
        errorHandler(err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, touched, errors, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

  const handleTabChange = (event, newTabIndex) => {
    setTabIndex(newTabIndex);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  return (
    <Card>
      <CardContent>
        <FormikProvider value={formik}>
          <Typography variant="h4" gutterBottom>
            Global Settings
          </Typography>
          <br />
          <AppBar position="static">
            <Tabs value={tabIndex} onChange={handleTabChange}>
              <Tab label="Global Settings" />
              <Tab label="Contact Us" />
            </Tabs>
          </AppBar>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {tabIndex === 0 && (
                  <>
                    <Box>
                      <br />
                      <Typography variant="h6" gutterBottom>
                        Logo
                      </Typography>
                      <Box display="flex" alignItems="center" gap={3} mb={2}>
                        {logoPreview && (
                          <img
                            src={logoPreview}
                            alt="Logo Preview"
                            style={{
                              width: "120px",
                              height: "auto",
                              border: "1px solid #ddd",
                              padding: "4px",
                              borderRadius: "6px",
                              backgroundColor: "#fff",
                            }}
                          />
                        )}
                        <Button variant="contained" component="label">
                          Upload New Logo
                          <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                        </Button>
                      </Box>

                      <Typography variant="h6" gutterBottom>
                        Social Media
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          { name: "fbLink", label: "Facebook Link *" },
                          { name: "twitterLink", label: "Twitter Link *" },
                          { name: "instaLink", label: "Instagram Link *" },
                          { name: "linkedInLink", label: "LinkedIn Link *" },
                        ].map((field) => (
                          <Grid item xs={12} sm={6} key={field.name}>
                            <TextField
                              fullWidth
                              name={field.name}
                              label={field.label}
                              {...getFieldProps(field.name)}
                              error={Boolean(touched[field.name] && errors[field.name])}
                              helperText={touched[field.name] && errors[field.name]}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </>
                )}

                {tabIndex === 1 && (
                  <>
                    <Box>
                      <br />
                      <Typography variant="h6" gutterBottom>
                        Contact Us
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="contactUsEmail"
                            label="Contact Email"
                            {...formik.getFieldProps("contactUsEmail")}
                            fullWidth
                            error={Boolean(touched.contactUsEmail && errors.contactUsEmail)}
                            helperText={touched.contactUsEmail && errors.contactUsEmail}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="phone"
                            label="Phone Number"
                            {...formik.getFieldProps("phone")}
                            fullWidth
                            error={Boolean(touched.phone && errors.phone)}
                            helperText={touched.phone && errors.phone}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}

                <Box display="flex" flexWrap="wrap" gap={2} justifyContent="flex-end" pt={2}>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    Update Settings
                  </Button>
                  <Button variant="text" onClick={() => navigate("/dashboard")}>
                    Cancel
                  </Button>
                </Box>
              </Stack>
            </Form>
          )}
        </FormikProvider>
      </CardContent>
    </Card>
  );
}

export default GlobalSetting;
